import { useCallback } from "react";
import {
  Person,
  useAssessmentLinkOrderMutation,
} from "@/features/profile/graphql/generated";
import { SurveyCompletion } from "@/features/people/graphql/generated";
import { getAssessmentSurveys } from "@/shared/utils/selectors/getSurveyTypes";
import { useToast } from "@/shared/contexts";
import { get } from "lodash";
import { AssessmentSurveyType } from "@/shared/constants/assessmentSurveys";

export const useAssessmentSurveys = () => {
  const [assessmentLinkOrderMutation, { loading }] =
    useAssessmentLinkOrderMutation();

  const { showToast } = useToast();

  const getAssessmentOrderUrl = useCallback(
    async (assessmentType: string, redirectUrl: string) => {
      try {
        const { data } = await assessmentLinkOrderMutation({
          variables: {
            assessmentType,
            redirectUrl,
          },
        });
        return data?.assessmentLinkOrder;
      } catch (err) {
        showToast({
          message: "Error!",
          description:
            get(err, "message") ||
            `${assessmentType} assessment survey not supported for this account`,
          type: "error",
        });
        return false;
      }
    },
    []
  );

  const handleRetakeSurvey = useCallback(
    async (assessmentType: string, pendingOrderLink?: string | null) => {
      // If there's already a pending order, use that link directly
      if (pendingOrderLink) {
        window.location.assign(pendingOrderLink);
        return;
      }

      // Otherwise create a new assessment order
      const assessmentLink = await getAssessmentOrderUrl(
        assessmentType,
        window.location.href
      );
      if (assessmentLink) {
        window.location.assign(assessmentLink);
      }
    },
    [getAssessmentOrderUrl]
  );

  const getPersonAssessmentSurveys = (
    person: Person,
    surveyTypes?: AssessmentSurveyType[]
  ) => {
    // Person surveys list that they are required to complete
    const personSurveys = (person?.survey_completion ||
      []) as SurveyCompletion[];

    return getAssessmentSurveys(personSurveys, surveyTypes);
  };

  return {
    getAssessmentOrderUrl,
    handleRetakeSurvey,
    getPersonAssessmentSurveys,
    loading,
  };
};
